/**
 * Panneau "Pas d'équipe" (espace compétition)
 *
 * Proposé quand le joueur ne fait partie d'aucune équipe : créer une équipe,
 * poster une annonce "recherche équipe", ou postuler directement à une
 * annonce "recherche joueur" en cours.
 */

import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import {
  createTeam, createAnnonce, getAnnonces, postulerAnnonce, type Annonce,
} from '../../lib/teamsApi';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

interface NoTeamPanelProps {
  session: Session;
  onTeamCreated: () => void;
}

type Choice = 'menu' | 'create' | 'apply';

export default function NoTeamPanel({ session, onTeamCreated }: NoTeamPanelProps) {
  const [choice, setChoice] = useState<Choice>('menu');
  const [nom, setNom] = useState('');
  const [devise, setDevise] = useState('');
  const [annonceTitre, setAnnonceTitre] = useState('');
  const [annonceContenu, setAnnonceContenu] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openings, setOpenings] = useState<Annonce[]>([]);
  const [loadingOpenings, setLoadingOpenings] = useState(true);

  const loadOpenings = useCallback(() => {
    setLoadingOpenings(true);
    getAnnonces({ annonce: 'joueur', state: 'ouvert', limit: 30 })
      .then((d) => setOpenings(d.items))
      .catch(() => setOpenings([]))
      .finally(() => setLoadingOpenings(false));
  }, []);

  useEffect(() => { loadOpenings(); }, [loadOpenings]);

  const submitCreate = async () => {
    if (!session.token || !nom.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createTeam(session.token, nom.trim(), devise.trim() || undefined);
      onTeamCreated();
    } catch {
      setError('Impossible de créer l\'équipe (nom peut-être déjà utilisé)');
    } finally {
      setBusy(false);
    }
  };

  const submitAnnonce = async () => {
    if (!session.token || !annonceTitre.trim() || !annonceContenu.trim()) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await createAnnonce(session.token, {
        titre: annonceTitre.trim(), contenu: annonceContenu.trim(), annonce: 'equipe',
      });
      setSuccess('Votre annonce a été publiée dans les petites annonces.');
      setAnnonceTitre('');
      setAnnonceContenu('');
      setChoice('menu');
    } catch {
      setError('Impossible de publier l\'annonce');
    } finally {
      setBusy(false);
    }
  };

  const applyTo = async (id: number) => {
    if (!session.token) return;
    setBusy(true);
    setError(null);
    try {
      await postulerAnnonce(session.token, id);
      setSuccess('Candidature envoyée !');
      loadOpenings();
    } catch {
      setError('Impossible de postuler à cette annonce');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="compet-page">
      <div className="compet-content" style={{ padding: 16 }}>
        {error && <div className="compet-error" style={{ marginBottom: 12 }}>{error}</div>}
        {success && (
          <div className="compet-error" style={{ marginBottom: 12, color: 'var(--success-color, #2e7d32)' }}>
            {success}
          </div>
        )}

        {choice === 'menu' && (
          <>
            <div className="tc-teams-grid" style={{ marginBottom: 24 }}>
              <button className="competition-card" onClick={() => setChoice('create')}>
                <div className="competition-card-text">
                  <span className="competition-card-title">Créer une équipe</span>
                  <span className="competition-card-subtitle">Vous en devenez le capitaine</span>
                </div>
              </button>
              <button className="competition-card" onClick={() => setChoice('apply')}>
                <div className="competition-card-text">
                  <span className="competition-card-title">Poster une annonce</span>
                  <span className="competition-card-subtitle">Faites savoir que vous recherchez une équipe</span>
                </div>
              </button>
            </div>

            <h3 style={{ margin: '0 0 10px' }}>
              Équipes qui recrutent {!loadingOpenings && `(${openings.length})`}
            </h3>
            {loadingOpenings && <div className="compet-loading">Chargement…</div>}
            {!loadingOpenings && openings.length === 0 && (
              <div className="compet-empty">Aucune annonce de recrutement en cours</div>
            )}
            {!loadingOpenings && openings.length > 0 && (
              <div className="tc-teams-grid">
                {openings.map((a) => (
                  <div key={a.id} className="competition-card" style={{ cursor: 'default' }}>
                    <PlayerAvatar
                      pseudo={a.author?.team_nom || a.pseudo}
                      size={40}
                      photoUrl={a.author?.team_nom
                        ? `${BACKEND_URL}/api/avatars/team/${encodeURIComponent(a.author.team_nom)}?size=80`
                        : undefined}
                    />
                    <div className="competition-card-text" style={{ flex: 1 }}>
                      <span className="competition-card-title">{a.titre}</span>
                      <span className="competition-card-subtitle">
                        {a.author?.team_nom || a.pseudo_display || a.pseudo}
                      </span>
                    </div>
                    <button
                      disabled={busy}
                      onClick={() => applyTo(a.id)}
                      style={{
                        padding: '6px 12px', borderRadius: 8, border: '1px solid var(--accent, #4a90d9)',
                        background: 'var(--accent, #4a90d9)', color: '#fff', cursor: 'pointer', fontSize: '0.82rem',
                      }}
                    >
                      Postuler
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {choice === 'create' && (
          <div style={{ maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ margin: 0 }}>Créer une équipe</h3>
            <label>
              Nom de l'équipe
              <input
                type="text" value={nom} onChange={(e) => setNom(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)' }}
              />
            </label>
            <label>
              Devise (optionnel)
              <input
                type="text" value={devise} onChange={(e) => setDevise(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setChoice('menu')} disabled={busy}>Annuler</button>
              <button onClick={submitCreate} disabled={busy || !nom.trim()}>Créer l'équipe</button>
            </div>
          </div>
        )}

        {choice === 'apply' && (
          <div style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ margin: 0 }}>Poster une annonce "recherche équipe"</h3>
            <label>
              Titre
              <input
                type="text" value={annonceTitre} onChange={(e) => setAnnonceTitre(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)' }}
              />
            </label>
            <label>
              Message
              <textarea
                value={annonceContenu} onChange={(e) => setAnnonceContenu(e.target.value)}
                rows={4}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color)', resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setChoice('menu')} disabled={busy}>Annuler</button>
              <button onClick={submitAnnonce} disabled={busy || !annonceTitre.trim() || !annonceContenu.trim()}>
                Publier l'annonce
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
